/**
 * Application Management Component
 * Main component for managing member applications
 */

// React and core imports
import React, { useState, useEffect, useCallback } from 'react';

// Custom hooks and services
import { useNotifications } from '../common/NotificationSystem';
import { useRBAC } from '../../hooks/useRBAC';
import applicationService from '../../services/applicationService';
import { getQuickFilters } from '../../utils/filterUtils';

// Application-specific components
import ApplicationList from './ApplicationList';
import ApplicationForm from './ApplicationForm';
import ApplicationFilters from './ApplicationFilters';
import ApplicationStats from './ApplicationStats';
import ApplicationDetailModal from './ApplicationDetailModal';

// Common/shared components
import ProfessionalLoader from '../common/ProfessionalLoader';
import UnifiedHeader from '../common/UnifiedHeader';
import UnifiedStats from '../common/UnifiedStats';
import UnifiedFilters from '../common/UnifiedFilters';
import UnifiedActionPanel from '../common/UnifiedActionPanel';
import UnifiedTable from '../common/UnifiedTable';
import UnifiedEmptyState from '../common/UnifiedEmptyState';

// Constants and styles
import { APPLICATION_STATUS } from '../../config/constants';
import './ApplicationManagement.css';

const ApplicationManagement = () => {
  const { showSuccess, showError } = useNotifications();
  const { canManageApplications, canApproveApplications, canDeleteApplications } = useRBAC();

  // State management
  const [applications, setApplications] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedApplications, setSelectedApplications] = useState([]);
  const [selectedAction, setSelectedAction] = useState('');


  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState(null);

  // Filter and search states
  const [searchParams, setSearchParams] = useState({
    search: '',
    status: '',
    state: '',
    city: '',
    created_after: '',
    created_before: '',
    ordering: '-created_at'
  });
  const [searchInput, setSearchInput] = useState('');

  // Pagination and stats
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 25,
    totalCount: 0,
    totalPages: 0
  });

  const [stats, setStats] = useState({
    total_applications: 0,
    pending_applications: 0,
    approved_applications: 0,
    rejected_applications: 0,
    applications_by_state: {},
    applications_by_month: {}
  });

  // Load applications
  const loadApplications = useCallback(async (isSearch = false) => {
    try {
      if (isSearch) {
        setIsProcessing(true);
      } else {
        setIsLoading(true);
      }


      const response = await applicationService.getApplications({
        ...searchParams,
        page: pagination.page,
        page_size: pagination.pageSize
      });


      if (response && response.success) {
        const applicationsData = response.data || [];

        // Handle different response structures
        let applicationsList = [];
        if (applicationsData && applicationsData.results) {
          // Paginated response structure
          applicationsList = applicationsData.results;
        } else if (Array.isArray(applicationsData)) {
          // Direct array response (current API structure)
          applicationsList = applicationsData;
        } else {
          applicationsList = [];
        }

        setApplications(applicationsList);

        // Update pagination based on API response structure
        if (applicationsData && applicationsData.count !== undefined) {
          setPagination(prev => ({
            ...prev,
            totalCount: applicationsData.count,
            totalPages: Math.ceil(applicationsData.count / prev.pageSize)
          }));
        } else {
          // For direct array responses, set pagination based on array length
          setPagination(prev => ({
            ...prev,
            totalCount: applicationsList.length,
            totalPages: Math.ceil(applicationsList.length / prev.pageSize)
          }));
        }
      } else {
        const errorMessage = response?.message || response?.error || 'Failed to load applications';
        showError(errorMessage, 'Error');
        setApplications([]);
      }
    } catch (error) {
      showError('Failed to load applications', 'Error');
      setApplications([]);
    } finally {
      setIsLoading(false);
      setIsProcessing(false);
    }
  }, [searchParams, pagination.page, pagination.pageSize, showError]);

  // Load statistics
  const loadStats = useCallback(async () => {
    try {
      const response = await applicationService.getMemberStatistics();
      if (response.success) {
        const statsData = response.data || {};

        // Map the actual API response structure to expected format
        const mappedStats = {
          total_applications: statsData.members?.total || 0,
          pending_applications: statsData.members?.pending || 0,
          approved_applications: statsData.members?.approved || 0,
          rejected_applications: statsData.members?.rejected || 0,
          applications_by_state: statsData.members?.by_state || {},
          applications_by_month: statsData.members?.by_month || {},
          // Additional stats from the API
          recent_applications: statsData.members?.recent_30_days || 0,
          mailchimp_sync: statsData.mailchimp_sync || {},
          email_subscriptions: statsData.email_subscriptions || {},
          invoices: statsData.invoices || {},
          xero_sync: statsData.xero_sync || {},
          bulk_operations: statsData.bulk_operations || {}
        };

        setStats(mappedStats);
      } else {
      }
    } catch (error) {
      // Stats loading failure is not critical
    }
  }, []);

  // Reload all data efficiently
  const reloadAllData = useCallback(async () => {
    try {
      const [applicationsResponse, statsResponse] = await Promise.all([
        applicationService.getApplications({
          ...searchParams,
          page: pagination.page,
          page_size: pagination.pageSize
        }),
        applicationService.getMemberStatistics()
      ]);

      if (applicationsResponse.success) {
        const applicationsData = applicationsResponse.data || [];
        setApplications(applicationsData.results || applicationsData);

        if (applicationsData.count !== undefined) {
          setPagination(prev => ({
            ...prev,
            totalCount: applicationsData.count,
            totalPages: Math.ceil(applicationsData.count / prev.pageSize)
          }));
        }
      }

      if (statsResponse.success) {
        setStats(statsResponse.data || {
          total_applications: 0,
          pending_applications: 0,
          approved_applications: 0,
          rejected_applications: 0,
          applications_by_state: {},
          applications_by_month: {}
        });
      }
    } catch (error) {
      // Error reloading data
    }
  }, [searchParams, pagination.page, pagination.pageSize]);

  // Load data on component mount and when dependencies change
  useEffect(() => {
    loadApplications();
  }, [loadApplications]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  // Handle search
  const handleSearch = useCallback((newSearchParams) => {
    setSearchParams(prev => ({ ...prev, ...newSearchParams }));
    setPagination(prev => ({ ...prev, page: 1 }));
  }, []);

  // Sync search input when searchParams.search is changed externally (e.g. quick filter "All")
  useEffect(() => {
    setSearchInput(searchParams.search || '');
  }, [searchParams.search]);

  // Search runs only on submit (search icon click or Enter key)
  const handleSearchInputChange = useCallback((value) => {
    setSearchInput(value);
  }, []);

  const handleSearchSubmit = useCallback(() => {
    handleSearch({ search: searchInput });
  }, [handleSearch, searchInput]);

  // Handle pagination
  const handlePageChange = useCallback((newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  }, []);

  const handlePageSizeChange = useCallback((newPageSize) => {
    setPagination(prev => ({ ...prev, pageSize: newPageSize, page: 1 }));
  }, []);

  // Handle application selection
  const handleApplicationSelect = useCallback((applicationId, isSelected) => {
    setSelectedApplications(prev => {
      if (isSelected) {
        return [...prev, applicationId];
      } else {
        return prev.filter(id => id !== applicationId);
      }
    });
  }, []);

  const handleSelectAll = useCallback((isSelected) => {
    if (isSelected) {
      setSelectedApplications(applications.map(app => app.id));
    } else {
      setSelectedApplications([]);
    }
  }, [applications]);

  // Handle application actions
  const handleCreateApplication = useCallback(async (applicationData) => {
    try {
      setIsProcessing(true);
      const response = await applicationService.createApplication(applicationData);

      if (response.success) {
        showSuccess('Application created successfully');
        setShowCreateModal(false);
        // Reload data after successful creation
        await reloadAllData();
      } else {
        showError(response.message || 'Failed to create application');
      }
    } catch (error) {
      showError('Failed to create application');
    } finally {
      setIsProcessing(false);
    }
  }, [showSuccess, showError, reloadAllData]);

  const handleUpdateApplication = useCallback(async (applicationId, applicationData) => {
    try {
      setIsProcessing(true);
      const response = await applicationService.updateApplication(applicationId, applicationData);

      if (response.success) {
        showSuccess('Application updated successfully');
        setShowEditModal(false);
        setSelectedApplication(null);
        // Reload data after successful update
        await reloadAllData();
      } else {
        showError(response.message || 'Failed to update application');
      }
    } catch (error) {
      showError('Failed to update application');
    } finally {
      setIsProcessing(false);
    }
  }, [showSuccess, showError, reloadAllData]);

  const handleDeleteApplication = useCallback(async (applicationId) => {
    if (!canDeleteApplications) {
      showError('You do not have permission to delete applications');
      return;
    }

    if (!window.confirm('Are you sure you want to delete this application? This action cannot be undone.')) {
      return;
    }

    try {
      setIsProcessing(true);
      const response = await applicationService.deleteApplication(applicationId);

      if (response.success) {
        showSuccess('Application deleted successfully');
        // Reload data after successful deletion
        await reloadAllData();
      } else {
        showError(response.message || 'Failed to delete application');
      }
    } catch (error) {
      showError('Failed to delete application');
    } finally {
      setIsProcessing(false);
    }
  }, [canDeleteApplications, showSuccess, showError, reloadAllData]);

  const handleReviewApplication = useCallback(async (applicationId, reviewData) => {
    if (!canApproveApplications) {
      showError('You do not have permission to review applications');
      return;
    }

    try {
      setIsProcessing(true);
      const response = await applicationService.reviewApplication(applicationId, reviewData);

      if (response.success) {
        const message = reviewData.status === 'approved'
          ? `Application approved successfully. Member ID: ${response.data?.member_id || 'N/A'}`
          : `Application ${reviewData.status} successfully`;
        showSuccess(message);
        // Reload data after successful review
        await reloadAllData();
      } else {
        showError(response.message || 'Failed to review application');
      }
    } catch (error) {
      // Error reviewing application
      showError('Failed to review application');
    } finally {
      setIsProcessing(false);
    }
  }, [canApproveApplications, showSuccess, showError, reloadAllData]);

  // Handle bulk actions
  const handleBulkAction = useCallback(async () => {
    if (selectedApplications.length === 0) {
      showError('Please select applications to perform bulk action');
      return;
    }

    if (!selectedAction) {
      showError('Please select an action to perform');
      return;
    }

    if (!canManageApplications) {
      showError('You do not have permission to perform bulk actions');
      return;
    }

    try {
      setIsProcessing(true);

      // Process each selected application
      const promises = selectedApplications.map(applicationId => {
        switch (selectedAction) {
          case 'approve':
            return applicationService.reviewApplication(applicationId, { status: APPLICATION_STATUS.APPROVED });
          case 'reject':
            return applicationService.reviewApplication(applicationId, { status: APPLICATION_STATUS.REJECTED });
          case 'delete':
            return applicationService.deleteApplication(applicationId);
          default:
            return Promise.resolve({ success: false, message: 'Invalid action' });
        }
      });

      const results = await Promise.allSettled(promises);
      const successful = results.filter(result => result.status === 'fulfilled' && result.value.success).length;
      const failed = results.length - successful;

      if (successful > 0) {
        showSuccess(`Bulk action completed: ${successful} successful, ${failed} failed`);
        setSelectedApplications([]);
        setSelectedAction('');
        loadApplications();
        loadStats();
      } else {
        showError('Bulk action failed for all selected applications');
      }
    } catch (error) {
      showError('Failed to perform bulk action');
    } finally {
      setIsProcessing(false);
    }
  }, [selectedApplications, selectedAction, canManageApplications, showSuccess, showError, loadApplications, loadStats]);

  // Handle modal actions
  const handleEditApplication = useCallback((application) => {
    setSelectedApplication(application);
    setShowEditModal(true);
  }, []);

  const handleViewApplication = useCallback((application) => {
    setSelectedApplication(application);
    setShowDetailModal(true);
  }, []);

  // Handle send edit link
  const handleSendEditLink = useCallback(async (application, recipientEmail = null) => {
    try {
      setIsProcessing(true);
      const response = await applicationService.sendEditLink(application.id, recipientEmail);
      
      if (response.success) {
        showSuccess(response.message || `Edit link sent successfully to ${response.data?.recipient_email || 'customer'}`);
      } else {
        showError(response.message || 'Failed to send edit link');
      }
    } catch (error) {
      showError('Failed to send edit link');
    } finally {
      setIsProcessing(false);
    }
  }, [showSuccess, showError]);

  // Memoized renderCell function to prevent unnecessary re-renders
  const renderCell = useCallback((column, application) => {
    switch (column.key) {
      case 'member_id':
        return <span className="unified-member-id">{application.member_id || 'N/A'}</span>;
      case 'organization_name':
        return (
          <div className="unified-organization-info">
            <div className="unified-org-name">{application.organization_name}</div>
            {application.city && application.state && (
              <div className="unified-location-info">
                <div className="unified-city-state">{application.city}, {application.state}</div>
              </div>
            )}
          </div>
        );
      case 'president_name':
        return (
          <div className="unified-contact-info">
            <div className="unified-contact-name">{application.primary_contact_name || application.president_name}</div>
            <div className="unified-contact-email">
              <a href={`mailto:${application.primary_contact_email || application.president_email}`} className="unified-email-link">
                {application.primary_contact_email || application.president_email}
              </a>
            </div>
          </div>
        );
      case 'status':
        return (
          <span className={`unified-status-badge ${application.status}`}>
            {application.status_display || application.status}
          </span>
        );
      case 'created_at':
        return new Date(application.created_at).toLocaleDateString();
      case 'actions':
        return (
          <div className="action-buttons">
            <button
              className="btn btn-sm btn-outline"
              onClick={(e) => {
                e.stopPropagation();
                handleViewApplication(application);
              }}
              title="View Details"
            >
              👁️
            </button>

            {canManageApplications && (
              <>
                <button
                  className="btn btn-sm btn-outline"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEditApplication(application);
                  }}
                  title="Edit Application"
                >
                  ✏️
                </button>
                <button
                  className="btn btn-sm btn-outline"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSendEditLink(application);
                  }}
                  title="Send for Edit / Resend Form"
                  disabled={isProcessing}
                >
                  📧
                </button>
              </>
            )}

            {canApproveApplications && application.status === APPLICATION_STATUS.PENDING && (
              <>
                <button
                  className="btn btn-sm btn-success"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleReviewApplication(application.id, { status: APPLICATION_STATUS.APPROVED });
                  }}
                  title="Approve Application"
                >
                  ✅
                </button>
                <button
                  className="btn btn-sm btn-danger"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleReviewApplication(application.id, { status: APPLICATION_STATUS.REJECTED });
                  }}
                  title="Reject Application"
                >
                  ❌
                </button>
              </>
            )}

            {canDeleteApplications && (
              <button
                className="btn btn-sm btn-danger"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteApplication(application.id);
                }}
                title="Delete Application"
              >
                🗑️
              </button>
            )}
          </div>
        );
      default:
        return application[column.key];
    }
  }, [canManageApplications, canApproveApplications, canDeleteApplications, handleViewApplication, handleEditApplication, handleReviewApplication, handleDeleteApplication, handleSendEditLink, isProcessing]);

  if (isLoading) {
    return (
      <div className="application-management">
        <div className="loading-container">
          <ProfessionalLoader message="Loading applications..." size="large" variant="default" />
        </div>
      </div>
    );
  }

  return (
    <div className="application-management">
      {/* Applications Management Section */}
      <div className="applications-management-container">
        {/* Unified Header */}
        <UnifiedHeader
          title="Application Management"
          subtitle="Manage member applications and approvals"
          icon="📋"
          count={applications.length}
          countLabel="Total Applications"
          selectedCount={selectedApplications.length}
          selectedLabel="Selected"
        >
          {canManageApplications && (
            <button
              className="btn btn-primary"
              onClick={() => setShowCreateModal(true)}
              disabled={isProcessing}
            >
              ➕ Create Application
            </button>
          )}
        </UnifiedHeader>

        {/* Unified Statistics */}
        <UnifiedStats
          stats={[
            { title: '📋 Total Applications', value: stats.total_applications || 0 },
            { title: '⏳ Pending Applications', value: stats.pending_applications || 0 },
            { title: '✅ Approved Applications', value: stats.approved_applications || 0 },
            { title: '❌ Rejected Applications', value: stats.rejected_applications || 0 }
          ]}
        />

        {/* Unified Filters */}
        <UnifiedFilters
          searchValue={searchInput}
          onSearchChange={handleSearchInputChange}
          onSearchSubmit={handleSearchSubmit}
          quickFilters={getQuickFilters('applications', searchParams)}
          onQuickFilterClick={(filterKey) => {
            if (filterKey === 'all') {
              setSearchInput('');
              setSearchParams({
                search: '',
                status: '',
                state: '',
                city: '',
                created_after: '',
                created_before: '',
                ordering: '-created_at'
              });
            } else {
              handleSearch({ status: searchParams.status === filterKey ? '' : filterKey });
            }
          }}
        />

        {/* Unified Action Panel */}
        <UnifiedActionPanel
          selectedCount={selectedApplications.length}
          totalCount={applications.length}
          selectedItemsLabel="applications"
          onSelectAll={handleSelectAll}
          onClearSelection={() => setSelectedApplications([])}
          selectedAction={selectedAction}
          onActionChange={setSelectedAction}
          onExecuteAction={handleBulkAction}
          isProcessing={isProcessing}
          actionOptions={[
            ...(canApproveApplications ? [
              { value: 'approve', label: '✅ Approve Applications', disabled: false },
              { value: 'reject', label: '❌ Reject Applications', disabled: false }
            ] : []),
            ...(canDeleteApplications ? [
              { value: 'delete', label: '🗑️ Delete Applications', disabled: false }
            ] : [])
          ]}
        />

        {/* Applications Table */}
        {applications.length > 0 ? (
          <UnifiedTable
            columns={[
              { key: 'member_id', header: 'Member ID', className: 'unified-table-select-header' },
              { key: 'organization_name', header: 'Organization' },
              { key: 'president_name', header: 'Contact Person' },
              { key: 'status', header: 'Status' },
              { key: 'created_at', header: 'Created' },
              { key: 'actions', header: 'Actions' }
            ]}
            data={applications}
            onRowClick={handleViewApplication}
            onSelectRow={handleApplicationSelect}
            selectedRows={selectedApplications}
            renderCell={renderCell}
          />
        ) : (
          <UnifiedEmptyState
            icon="📋"
            title="No applications found"
            description="Try adjusting your search criteria or create a new application."
            actions={[
              ...(canManageApplications ? [{
                label: 'Create Application',
                icon: '➕',
                onClick: () => setShowCreateModal(true),
                variant: 'primary'
              }] : [])
            ]}
          />
        )}
      </div>

      {/* Modals */}
      {showCreateModal && (
        <ApplicationForm
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onSubmit={handleCreateApplication}
          isProcessing={isProcessing}
          title="Create Application"
          existingApplications={applications}
        />
      )}

      {showEditModal && selectedApplication && (
        <ApplicationForm
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setSelectedApplication(null);
          }}
          onSubmit={(data) => handleUpdateApplication(selectedApplication.id, data)}
          isProcessing={isProcessing}
          title="Edit Application"
          initialData={selectedApplication}
          existingApplications={applications}
        />
      )}

      {showDetailModal && selectedApplication && (
        <ApplicationDetailModal
          isOpen={showDetailModal}
          onClose={() => {
            setShowDetailModal(false);
            setSelectedApplication(null);
          }}
          application={selectedApplication}
          onEdit={handleEditApplication}
          onDelete={handleDeleteApplication}
          onReview={handleReviewApplication}
          canManageApplications={canManageApplications}
          canApproveApplications={canApproveApplications}
          canDeleteApplications={canDeleteApplications}
        />
      )}
    </div>
  );
};

export default ApplicationManagement;
